// The pristine default config. `clientConfig` (the module global) starts as a
// copy of this and is mutated by init(); createClient() resolves fresh scoped
// configs from this pristine template so a scoped instance never inherits a
// prior init()'s state.
export const DEFAULT_CLIENT_CONFIG = {
    // User configured settings
    name: '',
    logoUrl: '',
    // Bounded production is the endpoint default. Apps still choose one explicit
    // auth method at init time. Pass `network: 'bounded-staging'` to target staging.
    network: 'bounded-production',
    wsApiUrl: 'wss://realtime.bounded.sh',
    apiUrl: 'https://realtime.bounded.sh',
    authApiUrl: 'https://wallet-auth.bounded.sh',
    humanAuthApiUrl: 'https://auth.bounded.sh',
    functionsUrl: 'https://functions.bounded.sh',
    devApiUrl: 'https://dev-api.bounded.sh',
    appId: '',
    // No hidden modal fallback: browser clients start hosted Bounded Auth with
    // loginWithRedirect/loginWithPopup. For crypto/onchain wallet login use
    // authMethod:'phantom' (Solana / Phantom), and use signInAnonymously() for
    // zero-friction guest accounts. ('wallet' and 'mobile-wallet-adapter' are
    // accepted spellings of the same lane and canonicalize to 'phantom'.)
    authMethod: 'none',
    chain: '',
    rpcUrl: '',
    skipBackendInit: true,
    authProvider: null,
    isServer: false,
};
export let clientConfig = Object.assign({}, DEFAULT_CLIENT_CONFIG);
let isInitialized = false;
// Callers awaiting getConfig() before init() completes. Resolved synchronously
// by init() — the previous 50ms setInterval poll added up to 50ms of latency
// to the first SDK operation after init for no reason.
let initWaiters = [];
export const BOUNDED_NETWORKS = {
    'bounded-staging': {
        wsApiUrl: 'wss://realtime-staging.bounded.sh',
        apiUrl: 'https://realtime-staging.bounded.sh',
        authApiUrl: 'https://wallet-auth-staging.bounded.sh',
        humanAuthApiUrl: 'https://auth-staging.bounded.sh',
        functionsUrl: 'https://functions-staging.bounded.sh',
        devApiUrl: 'https://dev-api-staging.bounded.sh',
    },
    'bounded-production': {
        // All on bounded.sh custom domains. authApiUrl = WALLET/SIWS issuer
        // (wallet + guest providers); humanAuthApiUrl = human email-OTP issuer
        // (the 'email' provider). realtime.bounded.sh fronts the realtime worker.
        wsApiUrl: 'wss://realtime.bounded.sh',
        apiUrl: 'https://realtime.bounded.sh',
        authApiUrl: 'https://wallet-auth.bounded.sh',
        humanAuthApiUrl: 'https://auth.bounded.sh',
        functionsUrl: 'https://functions.bounded.sh',
        devApiUrl: 'https://dev-api.bounded.sh',
    },
};
// `network: 'bounded'` is an alias for the current GA target.
BOUNDED_NETWORKS['bounded'] = BOUNDED_NETWORKS['bounded-production'];
/**
 * The hosted `/.well-known/bounded-webhook-keys.json` URL for the configured
 * Bounded network, or `undefined` when no Bounded network is set. Used by
 * `verifyWebhook` so a staging receiver that did `init({ network:
 * 'bounded-staging' })` verifies against the STAGING signing keys instead of
 * the production default. Synchronous and non-blocking on purpose: a pure
 * webhook receiver may never call `init()`, so we read the already-applied
 * config rather than awaiting `getConfig()` (which blocks on initialization).
 * Returns `undefined` when the network is unknown so the caller keeps its
 * fail-closed production default (audit SDK LOW-7).
 */
export function getWebhookKeysUrl() {
    const net = clientConfig.network;
    const preset = net ? BOUNDED_NETWORKS[net] : undefined;
    const host = preset === null || preset === void 0 ? void 0 : preset.apiUrl;
    if (!host)
        return undefined;
    return host.replace(/\/$/, '') + '/.well-known/bounded-webhook-keys.json';
}
/**
 * The app id configured via `init({ appId })`, or `undefined` when none has been set.
 * Synchronous + non-blocking on purpose (mirrors {@link getWebhookKeysUrl}): a pure webhook
 * receiver may never call `init()`, and `getConfig()` would block on initialization. Used by
 * `verifyWebhook` to bind a webhook to THIS app by default (#072) rather than trusting any
 * app's shared-key-signed delivery.
 */
export function getConfiguredSessionNamespace() {
    return JSON.stringify([(clientConfig.humanAuthApiUrl || "https://auth.bounded.sh").replace(/\/$/, ""), clientConfig.appId]);
}
export function getConfiguredAppId() {
    return clientConfig.appId || undefined;
}
/**
 * True when init() configured a Bounded network (the Cloudflare-native stack).
 * Synchronous + non-blocking. Used to route reads/writes that behave differently
 * on Bounded vs the legacy backend (e.g. `count`/`aggregate`, which on
 * Bounded must use the deterministic server aggregation, not the legacy AI query).
 */
export function isBoundedNetwork() {
    return isBoundedNetworkConfig(clientConfig);
}
/**
 * Config-scoped variant of {@link isBoundedNetwork}: reports whether a SPECIFIC
 * config (e.g. a scoped `createClient()` instance's) targets a Bounded network,
 * rather than the module global. Used so a scoped client routes count/aggregate
 * by its OWN network, not the global one.
 */
export function isBoundedNetworkConfig(config) {
    const net = config.network;
    return typeof net === 'string' && net in BOUNDED_NETWORKS;
}
/**
 * Merge a partial config over a base config, applying the Bounded endpoint
 * precedence rule used everywhere: defaults (or `base`) < network preset <
 * explicit fields. Pure - never touches module state. Shared by `init` (base =
 * the current global) and `createClient` (base = the pristine defaults) so both
 * resolve endpoints identically. Throws on an unsupported `network`.
 */
export function resolveClientConfig(base, newConfig) {
    if (newConfig.network !== undefined && !(newConfig.network in BOUNDED_NETWORKS)) {
        throw new Error(`Unsupported Bounded network "${String(newConfig.network)}". Expected bounded, bounded-staging, or bounded-production.`);
    }
    const preset = newConfig.network ? BOUNDED_NETWORKS[newConfig.network] : {};
    return Object.assign(Object.assign(Object.assign({}, base), preset), newConfig);
}
export function init(newConfig) {
    return new Promise((resolve, reject) => {
        if (!newConfig.appId) {
            reject(new Error('No app ID provided.'));
            return;
        }
        // Bounded is client-driven: defaults are Bounded production, `network`
        // switches the whole endpoint set (e.g. 'bounded-staging'), and anything
        // passed explicitly wins. No `/config` round-trip; browser SDKs still pass
        // one explicit authMethod so there is no hidden auth-provider fallback.
        //   defaults (bounded-production) < network preset < explicit newConfig
        let resolved;
        try {
            resolved = resolveClientConfig(clientConfig, newConfig);
        }
        catch (error) {
            reject(error instanceof Error ? error : new Error(String(error)));
            return;
        }
        clientConfig = resolved;
        isInitialized = true;
        const waiters = initWaiters;
        initWaiters = [];
        for (const wake of waiters)
            wake();
        resolve();
    });
}
function waitForInitialization() {
    if (isInitialized)
        return Promise.resolve();
    return new Promise((resolve) => { initWaiters.push(resolve); });
}
export async function getConfig() {
    await waitForInitialization();
    return clientConfig;
}
